import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { DocumentTableRow } from './table';
import { DocumentTableFilters } from './table/DocumentTableFilters';
import { DocumentTableHeader } from './table/DocumentTableHeader';
import { DocumentTablePagination } from './table/DocumentTablePagination';
import type { DocumentRowActions, DocumentRowPermissions, StatusFilter } from './table';

interface DocumentTableProps {
  documents: DocumentNodeWithDetailsDto[];
  canRename?: boolean;
  canDelete?: boolean;
  canDiffuse?: boolean;
  canSendForValidation?: boolean;
  canValidate?: boolean;
  canUploadNewVersion?: boolean;
  canArchive?: boolean;
  loadingDocumentId?: string | null;
  highlightedNodeId?: string | null;
  onOpen: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
  onDiffuse?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onResend?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onSendForValidation?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onValidate?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onReject?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onUploadNewVersion?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onArchive?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
  onShowReaders?: ((doc: DocumentNodeWithDetailsDto) => void) | undefined;
}

const ITEMS_PER_PAGE = 10;

export const DocumentTable = ({
  documents,
  canRename = false,
  canDelete = false,
  canDiffuse = false,
  canSendForValidation = false,
  canValidate = false,
  canUploadNewVersion = false,
  canArchive = false,
  loadingDocumentId,
  highlightedNodeId,
  onOpen,
  onRename,
  onDelete,
  onDiffuse,
  onResend,
  onSendForValidation,
  onValidate,
  onReject,
  onUploadNewVersion,
  onArchive,
  onShowReaders,
}: DocumentTableProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const permissions: DocumentRowPermissions = useMemo(
    () => ({
      canRename,
      canDelete,
      canDiffuse,
      canSendForValidation,
      canValidate,
      canUploadNewVersion,
      canArchive,
    }),
    [
      canRename,
      canDelete,
      canDiffuse,
      canSendForValidation,
      canValidate,
      canUploadNewVersion,
      canArchive,
    ],
  );

  const actions: DocumentRowActions = useMemo(
    () => ({
      onOpen,
      onRename,
      onDelete,
      onDiffuse,
      onResend,
      onSendForValidation,
      onValidate,
      onReject,
      onUploadNewVersion,
      onArchive,
      onShowReaders,
    }),
    [
      onOpen,
      onRename,
      onDelete,
      onDiffuse,
      onResend,
      onSendForValidation,
      onValidate,
      onReject,
      onUploadNewVersion,
      onArchive,
      onShowReaders,
    ],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [documents, searchQuery, statusFilter]);

  const highlightedIndex = useMemo(
    () =>
      highlightedNodeId ? filteredDocuments.findIndex((doc) => doc.id === highlightedNodeId) : -1,
    [filteredDocuments, highlightedNodeId],
  );

  const jumpedToNodeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightedNodeId || highlightedIndex < 0) {
      return;
    }
    if (jumpedToNodeIdRef.current === highlightedNodeId) {
      return;
    }
    jumpedToNodeIdRef.current = highlightedNodeId;
    setCurrentPage(Math.floor(highlightedIndex / ITEMS_PER_PAGE) + 1);
  }, [highlightedNodeId, highlightedIndex]);

  const totalPages = Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE);
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleSelectAll = () => {
    if (selectedIds.size === paginatedDocuments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedDocuments.map((d) => d.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <DocumentTableFilters
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        onSearchChange={setSearchQuery}
        onStatusChange={setStatusFilter}
        onReset={handleReset}
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
        <table className="block w-full lg:table">
          <DocumentTableHeader
            isAllSelected={
              selectedIds.size === paginatedDocuments.length && paginatedDocuments.length > 0
            }
            onSelectAll={handleSelectAll}
          />
          <tbody className="block lg:table-row-group">
            {paginatedDocuments.map((doc) => (
              <DocumentTableRow
                key={doc.id}
                document={doc}
                isSelected={selectedIds.has(doc.id)}
                isMenuOpen={openMenuId === doc.id}
                permissions={permissions}
                actions={actions}
                loadingDocumentId={loadingDocumentId}
                isHighlighted={doc.id === highlightedNodeId}
                onSelect={handleSelectOne}
                onMenuToggle={setOpenMenuId}
                menuRef={menuRef}
              />
            ))}
          </tbody>
        </table>

        {paginatedDocuments.length === 0 && (
          <div className="py-12 text-center text-gray-500">{t('documents.empty.title')}</div>
        )}
      </div>

      <DocumentTablePagination
        selectedCount={selectedIds.size}
        totalCount={filteredDocuments.length}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};
